import { Eye, EyeOff, Sparkles } from 'lucide-react';
import { forwardRef, useCallback, useState, type ComponentProps } from 'react';
import { generateStaffPassword } from '../lib/generate-staff-password';
import { cn } from '../lib/cn';
import { Input } from './Input';

export type PasswordInputProps = Omit<ComponentProps<typeof Input>, 'type'> & {
  /** Рядом с полем — только иконка генерации пароля (канон staff, кроме экрана входа). */
  showGenerateButton?: boolean;
  /** Подписи кнопки «глаз» (для API-ключей и токенов — не «пароль»). */
  visibilityToggleAriaLabels?: { show: string; hide: string };
};

export const PasswordInput = forwardRef<HTMLInputElement, PasswordInputProps>(function PasswordInput(
  {
    className,
    disabled,
    readOnly,
    showGenerateButton,
    visibilityToggleAriaLabels,
    onChange,
    ...props
  },
  ref
) {
  const [visible, setVisible] = useState(false);
  const canGenerate = Boolean(showGenerateButton && !disabled && !readOnly);
  const ariaShow = visibilityToggleAriaLabels?.show ?? 'Показать пароль';
  const ariaHide = visibilityToggleAriaLabels?.hide ?? 'Скрыть пароль';

  const applyGenerated = useCallback(() => {
    if (!canGenerate || !onChange) return;
    const next = generateStaffPassword();
    onChange({ target: { value: next } } as React.ChangeEvent<HTMLInputElement>);
    setVisible(true);
  }, [canGenerate, onChange]);

  const padRight = showGenerateButton ? 'pr-[4.75rem]' : 'pr-10';

  return (
    <div className="relative">
      <Input
        ref={ref}
        type={visible ? 'text' : 'password'}
        className={cn(padRight, className)}
        disabled={disabled}
        readOnly={readOnly}
        onChange={onChange}
        {...props}
      />
      {showGenerateButton ? (
        <button
          type="button"
          className="absolute right-10 top-1/2 inline-flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-sm text-[hsl(var(--ui-muted-foreground))] transition-colors hover:text-[hsl(var(--ui-foreground))] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--ui-ring))] disabled:pointer-events-none disabled:opacity-50"
          disabled={disabled || readOnly}
          onMouseDown={(e) => e.preventDefault()}
          onClick={applyGenerated}
          aria-label="Сгенерировать пароль"
          title="Сгенерировать пароль"
        >
          <Sparkles className="size-4" aria-hidden />
        </button>
      ) : null}
      <button
        type="button"
        className="absolute right-1 top-1/2 inline-flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-sm text-[hsl(var(--ui-muted-foreground))] transition-colors hover:text-[hsl(var(--ui-foreground))] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--ui-ring))] disabled:pointer-events-none disabled:opacity-50"
        disabled={disabled}
        onMouseDown={(e) => e.preventDefault()}
        onClick={() => setVisible((v) => !v)}
        aria-label={visible ? ariaHide : ariaShow}
      >
        {visible ? <EyeOff className="size-4" aria-hidden /> : <Eye className="size-4" aria-hidden />}
      </button>
    </div>
  );
});
